import {
  RequestBase, RequestBasePage, RequestBasePageIds, RequestBaseSet,
  ResponseBase, ResponseBasePage, ResponseBaseSet
} from "src/app/modules/fwcore/models";
import {
  HelpAncestor, HelpCategory, HelpContextItem, HelpCoverageRow, HelpItem, HelpItemAdmin,
  HelpRelated, HelpSearchResult, HelpTreeNode, HelpType
} from "src/app/features/prob/models";

/**
 * Request the Help node-kind lookup.
 */
export class RequestHelpType extends RequestBase {
}

/**
 * Response containing the Help node kinds.
 */
export class ResponseHelpType extends ResponseBasePage<HelpType> {
}

/**
 * Request the Help content-category lookup.
 */
export class RequestHelpCategory extends RequestBase {
}

/**
 * Response containing the Help content categories.
 *
 * An empty data array is a NORMAL result, not a failure - the table can hold no rows.
 */
export class ResponseHelpCategory extends ResponseBasePage<HelpCategory> {
}

/**
 * Request the Help coverage gap report.
 */
export class RequestHelpCoverage extends RequestBase {

  /**
   * One kind of gap to report: 'UnreachableItem', 'DuplicateHint' or 'EmptyItem'.
   * Null or omitted reports all three.
   */
  issue?: string | null;
}

/**
 * Response containing the Help coverage gaps.
 *
 * An empty data array means no gaps were found, which is the goal state.
 */
export class ResponseHelpCoverage extends ResponseBasePage<HelpCoverageRow> {
}

/**
 * Request the Help hierarchy that drives the Help navigation.
 *
 * Extends RequestBase rather than RequestBasePage because the tree is not paged - it
 * is fetched whole and shaped client-side.
 */
export class RequestHelpTree extends RequestBase {

  /** Restrict to one section. Omit for every section. */
  sectionId?: string | null;

  /** Filter to one node kind - 'def', 'nav' or 'struct'. */
  helpTypeCode?: string | null;

  /** How deep to walk. 0 returns section roots only; omit for all. */
  maxDepth?: number | null;

  /**
   * Include items an admin has parked. Reader callers must leave this unset - the
   * reader endpoint forces it off server-side regardless.
   */
  includeInactive?: boolean | null;
}

/**
 * Response containing the Help hierarchy as a flat, depth-first ordered list.
 *
 * Flat rather than nested on purpose: every node carries its parentId, depth and
 * breadcrumb and the rows arrive depth-first, so the caller can build whatever shape
 * it needs.
 */
export class ResponseHelpTree extends ResponseBasePage<HelpTreeNode> {
}

/**
 * Request Help articles with their bodies, by ids or by parent.
 */
export class RequestHelpItem extends RequestBasePageIds {

  /** Return the immediate children of this item. Ignored when ids is supplied. */
  parentId?: string | null;

  /** Only 'label' and 'sortOrder' are honoured; anything else falls back to sortOrder. */
  sortBy?: string | null;
}

/**
 * Response containing Help articles as a reader may see them.
 */
export class ResponseHelpItem extends ResponseBasePage<HelpItem> {

  /**
   * The requested item's ancestor trail, root first.
   *
   * Populated only for a single-item request - a trail for a list of items would be
   * meaningless, so its absence on a multi-item request is normal, not a failure.
   */
  ancestors?: HelpAncestor[] | null;
}

/**
 * Ask which Help article answers a given screen context.
 */
export class RequestHelpContext extends RequestBase {

  /** The most specific key the screen knows, e.g. 'tower.workplace'. */
  linkVarName?: string | null;

  /** The route the user is on. Supplying only this is a valid call. */
  linkUrlPath?: string | null;

  /** Coarse category. Unused for now - no help item carries a category code yet. */
  helpCategoryCode?: string | null;

  /** How many related siblings to return. */
  maxRelated?: number | null;

  /** Whether the ancestor trail and related siblings come back. */
  includeRelated?: boolean | null;
}

/**
 * Response containing the article resolved for a screen context.
 */
export class ResponseHelpContext extends ResponseBase {

  /**
   * The resolved article.
   *
   * Resolution falls back rather than failing, so this is normally populated - but it
   * is null when there is no content at all to fall back to. Handle that rather than
   * trusting the fallback.
   */
  data?: HelpContextItem | null;

  ancestors?: HelpAncestor[] | null;
  related?: HelpRelated[] | null;
}

/**
 * Search Help, either within one section or across all of it.
 */
export class RequestHelpSearch extends RequestBasePage {

  /** Fewer than two characters returns no rows rather than an error. */
  query?: string | null;

  /** The section to search within. Omit to search all of Help. */
  sectionId?: string | null;
}

/**
 * Response containing Help search hits, ranked with title matches above body matches.
 */
export class ResponseHelpSearch extends ResponseBasePage<HelpSearchResult> {
}

/**
 * Create or update Help item(s).
 *
 * The concurrency token is HelpItem.concurrencyToken on each item - the value that
 * was issued when it was loaded. Send it back unchanged; a stale one is refused with
 * 409 rather than silently overwriting another author.
 */
export class RequestHelpItemSet extends RequestBaseSet<HelpItemAdmin> {

  /**
   * Whether parentId on the supplied items is meant to MOVE them.
   *
   * Off by default and deliberately explicit: on an update a null column means
   * "leave unchanged", so a save that simply did not populate parentId would
   * otherwise re-parent the item to a root by accident.
   */
  reparent?: boolean | null;
}

/**
 * Response for creates or updates applied to Help items.
 */
export class ResponseHelpItemSet extends ResponseBaseSet<HelpItemAdmin> {
}

/**
 * Permanently delete one Help item and everything beneath it.
 *
 * Two-phase: call with force false first, show the descendant count, and only then
 * call again with true. Deletes are permanent, cascading and unlogged - no recycle
 * bin, no history - so that confirmation is the only safeguard.
 */
export class RequestHelpItemDelete extends RequestBase {

  id?: string | null;

  /** Whether the user has confirmed and the delete should proceed. */
  force?: boolean | null;
}

/**
 * Result of a Help item delete, whether it asked for confirmation or carried it out.
 *
 * Both phases return the same shape, so branch on requiresConfirmation rather than
 * on which call was made.
 */
export class ResponseHelpItemDelete extends ResponseBase {

  requiresConfirmation?: boolean | null;
  descendantCount?: number | null;
  deletedCount?: number | null;
}

/**
 * Reorder the children of one Help item.
 *
 * The whole new sibling order goes in one call, so the order is never briefly
 * inconsistent. Every id must already be a child of parentId; the server refuses the
 * call otherwise rather than reordering another parent's children.
 */
export class RequestHelpItemSort extends RequestBase {

  parentId?: string | null;
  sortedIds?: string[] | null;
}

/**
 * Response for a Help item reorder.
 */
export class ResponseHelpItemSort extends ResponseBase {
}

/**
 * Response containing Help items as an administrator sees them.
 */
export class ResponseHelpItemAdmin extends ResponseBasePage<HelpItemAdmin> {

  /** Ancestor trail of the requested item, populated only for a single-item request. */
  ancestors?: HelpAncestor[] | null;
}
